import 'flowbite'
import { createEffect, createSignal, onCleanup, onMount, Show, type Component } from 'solid-js'
import { Icon } from '@iconify-icon/solid'
import { getDateFormatString, theDayBefore } from '../../../shared/helpers/date.functions'
import { useDateFormatter } from '../../../shared/helpers/date-formatter'
// @ts-expect-error Flowbite does not provide types
import DateRangePicker from 'flowbite-datepicker/DateRangePicker'
import Dialog from './Dialog/Dialog'
import Button, { ButtonStyle } from './Button'

export interface CustomRangeDialogProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onApply: (from: Date, to: Date) => void
  readonly firstAllowedDate?: Date
  readonly lastAllowedDate?: Date
  readonly initialFrom?: Date
  readonly initialTo?: Date
}

const CustomRangeDialog: Component<CustomRangeDialogProps> = (props) => {
  const { shortDateFormatter } = useDateFormatter()
  const [selectedDates, setSelectedDates] = createSignal<Array<Date | undefined>>([props.initialFrom, props.initialTo])

  let calendar: DateRangePicker | null = null
  let inputsRef: HTMLFormElement | null = null

  // Analytics data refreshes daily at 12:00 UTC with a one-day lag, so "today"
  // never has data. Clamp selection to the last complete day (yesterday); this
  // covers both the main custom range and the custom compare range dialogs.
  const yesterday = theDayBefore(new Date())
  const lastAllowedDate = props.lastAllowedDate && props.lastAllowedDate < yesterday ? props.lastAllowedDate : yesterday
  const firstOf2020 = new Date('2020-01-01')
  const firstAllowedDate = props.firstAllowedDate ?? firstOf2020

  const BaseCalendarOptions = {
    format: getDateFormatString(),
    orientation: 'bottom',
    autohide: true,
    rangePicker: true,
    allowOneSidedRange: true
  }

  onMount(() => {
    if (inputsRef && props.isOpen) {
      initializeCalendar()
    }
  })

  createEffect(() => {
    if (props.isOpen && inputsRef) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initializeCalendar()
      }, 50)
    } else if (!props.isOpen && calendar) {
      // Clean up when dialog closes
      calendar = null
    }
  })

  const initializeCalendar = () => {
    if (!inputsRef) return

    calendar = new DateRangePicker(inputsRef, {
      ...BaseCalendarOptions,
      minDate: shortDateFormatter(firstAllowedDate),
      maxDate: shortDateFormatter(lastAllowedDate)
    })

    const interval = setInterval(() => {
      if (calendar) {
        const dates: Date[] = calendar.getDates()
        if (dates && dates.some((date, index) => date?.getTime() !== selectedDates()[index]?.getTime())) {
          setSelectedDates(dates)
        }
      }
    }, 300)

    onCleanup(() => {
      clearInterval(interval)
    })
  }

  createEffect(() => {
    const [start, end] = selectedDates()
    if (start && !end && calendar) {
      const yearAfterStart = new Date(start)
      yearAfterStart.setFullYear(yearAfterStart.getFullYear() + 1)
      const maxDate = yearAfterStart > lastAllowedDate ? lastAllowedDate : yearAfterStart
      calendar.setOptions({
        ...BaseCalendarOptions,
        maxDate: shortDateFormatter(maxDate)
      })
    }

    if (!start && end && calendar) {
      const yearBeforeEnd = new Date(end)
      yearBeforeEnd.setFullYear(yearBeforeEnd.getFullYear() - 1)
      const minDate = yearBeforeEnd < firstAllowedDate ? firstAllowedDate : yearBeforeEnd
      calendar.setOptions({
        ...BaseCalendarOptions,
        minDate: shortDateFormatter(minDate)
      })
    }
  })

  const handleApply = (): void => {
    if (!calendar) return
    const [start, end] = calendar.getDates()
    if (start && end) {
      props.onApply(start, end)
      props.onClose()
    }
  }

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleApply()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      props.onClose()
    }
  }

  const isApplyDisabled = (): boolean => {
    const dates = selectedDates()
    return !dates.every((value) => !!value)
  }

  return (
    <Show when={props.isOpen}>
      <Dialog overflow contentFit>
        <div class="p-6 w-[440px] max-w-[calc(100vw-2rem)]">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Custom Range</h3>

          <form
            ref={(el) => (inputsRef = el)}
            id="custom-range-inputs"
            class="flex gap-4 items-center mb-3"
            onKeyDown={handleKeyDown}
          >
            <div class="flex flex-col gap-1 w-full">
              <label class="text-sm font-medium text-gray-700">
                From <span class="text-red-500">*</span>
              </label>
              <div class="relative rounded-md shadow-sm">
                <div class="text-gray-400 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon icon="heroicons:calendar-days-20-solid" class="text-[1rem]" />
                </div>
                <input
                  id="startDate"
                  name="range-start"
                  readOnly
                  placeholder="MM/DD/YY"
                  type="text"
                  class="w-full block text-sm rounded-lg border-gray-300 py-2 pl-10 pr-10 text-gray-900 placeholder:text-gray-400 shadow-sm focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:border-indigo-600"
                />
                <div class="text-gray-400 pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <Icon icon="heroicons:chevron-down-20-solid" class="text-[1rem]" />
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-1 w-full">
              <label class="text-sm font-medium text-gray-700">
                To <span class="text-red-500">*</span>
              </label>
              <div class="relative rounded-md shadow-sm">
                <div class="text-gray-400 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon icon="heroicons:calendar-days-20-solid" class="text-[1rem]" />
                </div>
                <input
                  id="endDate"
                  name="range-end"
                  readOnly
                  placeholder="MM/DD/YY"
                  type="text"
                  class="w-full block text-sm rounded-lg border-gray-300 py-2 pl-10 pr-10 text-gray-900 placeholder:text-gray-400 shadow-sm focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:border-indigo-600"
                />
                <div class="text-gray-400 pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <Icon icon="heroicons:chevron-down-20-solid" class="text-[1rem]" />
                </div>
              </div>
            </div>
          </form>

          <div class="flex justify-end gap-3 pt-3">
            <Button style={ButtonStyle.White} action={props.onClose}>
              Cancel
            </Button>
            <Button style={ButtonStyle.Primary} action={handleApply} disabled={isApplyDisabled()}>
              Apply
            </Button>
          </div>
        </div>
      </Dialog>
    </Show>
  )
}

export default CustomRangeDialog
